import { useGetIdentity, useTranslate } from 'react-admin';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';
import type { ShareAdjustmentProposal } from 'src/pages/admin/services/vaultService';
import { PermissionButton } from 'src/routes/components/permission-button';

import { formatInstant } from '../vaults/helpers';
import {
  RECONCILIATION_NS as NS,
  RECONCILIATION_PERMISSION_RESOURCE as RESOURCE,
} from './constants';
import { adjustmentActions } from './helpers';
import { breakLabel } from './Tables';
import type { ReconciliationActionsForm } from './useReconciliationActions';

const EMPTY = '—';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Stack>
);

/**
 * XBIT-4864: one proposal (and the break it names) with its actions. Approve & resolve is offered to a second operator only:
 * the proposer sees it disabled with the reason (the engine refuses self-approval too and its message is shown if a stale
 * page gets that far). Casbin: approve needs `approve`, cancel needs `propose`.
 */
export const ProposalDialog = ({
  proposal,
  actions,
  onClose,
}: {
  proposal: ShareAdjustmentProposal | null;
  actions: ReconciliationActionsForm;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const { data: identity } = useGetIdentity();
  const t = (key: string, options?: object) => translate(`${NS}.detail.${key}`, options);
  const operatorId = identity?.id === undefined ? undefined : String(identity.id);

  return (
    <Dialog open={proposal !== null} onClose={onClose} maxWidth="sm" fullWidth fullScreen={smDown}>
      {proposal && (
        <ProposalBody
          proposal={proposal}
          operatorId={operatorId}
          t={t}
          actions={actions}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};

const ProposalBody = ({
  proposal,
  operatorId,
  t,
  actions,
  onClose,
}: {
  proposal: ShareAdjustmentProposal;
  operatorId: string | undefined;
  t: (key: string, options?: object) => string;
  actions: ReconciliationActionsForm;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const can = adjustmentActions(proposal, operatorId);
  const awaiting = proposal.status === 'AWAITING_APPROVAL';
  const own = awaiting && !can.approve;
  return (
    <>
      <DialogTitle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{t('title', { id: proposal.id })}</span>
          <Chip
            size="small"
            variant="outlined"
            label={translate(`${NS}.status.${proposal.status}`)}
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Field label={t('break')}>{breakLabel(proposal)}</Field>
          <Field label={t('holder')}>{proposal.holderId ?? translate(`${NS}.shareNeutral`)}</Field>
          <Field label={t('delta')}>{proposal.deltaShares}</Field>
          {proposal.navApplied && (
            <Field label={t('navApplied')}>
              {proposal.navApplied}
              {proposal.navValuedAt && ` · ${formatInstant(proposal.navValuedAt)}`}
            </Field>
          )}
          <Field label={t('reasonCode')}>{proposal.reasonCode}</Field>
          <Field label={t('memo')}>{proposal.memo}</Field>
          <Field label={t('evidenceRef')}>{proposal.evidenceRef}</Field>
          <Field label={t('proposed')}>
            {proposal.proposedBy} · {formatInstant(proposal.proposedAt)}
          </Field>
          <Field label={t('approved')}>
            {proposal.approvedBy
              ? `${proposal.approvedBy} · ${proposal.approvedAt ? formatInstant(proposal.approvedAt) : EMPTY}`
              : EMPTY}
          </Field>
          {proposal.cancelledBy && (
            <Field label={t('cancelled')}>
              {proposal.cancelledBy} ·{' '}
              {proposal.cancelledAt ? formatInstant(proposal.cancelledAt) : EMPTY}
            </Field>
          )}
          {proposal.appliedJournalSeq != null && (
            <Field label={t('journalSeq')}>{proposal.appliedJournalSeq}</Field>
          )}
          {own && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t(operatorId ? 'ownProposal' : 'operatorUnknown')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
        {can.cancel && (
          <PermissionButton resource={RESOURCE} action="propose">
            <Button variant="outlined" onClick={() => actions.open('cancel', proposal)}>
              {t('cancel')}
            </Button>
          </PermissionButton>
        )}
        {awaiting && (
          <PermissionButton resource={RESOURCE} action="approve">
            <Tooltip title={own ? t(operatorId ? 'ownProposal' : 'operatorUnknown') : ''}>
              <span>
                <Button
                  variant="contained"
                  color="error"
                  disabled={!can.approve}
                  onClick={() => actions.open('approve', proposal)}
                >
                  {t('approve')}
                </Button>
              </span>
            </Tooltip>
          </PermissionButton>
        )}
      </DialogActions>
    </>
  );
};
